import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { ReviewWritePcForm } from './ReviewWritePcForm';
import { useQuery } from '@tanstack/react-query';
import { ProductApi } from '../../../api/productApi';
import type { SelectOption } from './common/Select';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Modal = styled(motion.div)`
  background: #fff;
  border-radius: 20px;
  width: 686px;
  max-width: 90vw;
  max-height: 90vh;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 0px 24px;
`;

const Title = styled.div`
  font-size: 20px;
  font-weight: 400;
  color: #2e2e2e;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px !important;
  font-weight: 400;
  color: #333;
  cursor: pointer;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CompleteContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 16px;
  padding: 60px 40px;
`;

const CompleteTitle = styled.div`
  color: #2e2e2e;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 32px;
  font-weight: 600;
  line-height: 140%;
`;

const CompleteDesc = styled.div`
  color: #737373;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 140%;
  margin-bottom: 24px;
`;

const CompleteButton = styled.button`
  width: 200px;
  height: 48px;
  background: #3d1690;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Plus Jakarta Sans', sans-serif;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

export interface ReviewWritePcModalValues {
  rating: number;
  title: string;
  content: string;
  nickname: string;
  skinType: string;
  skinConcern: string;
  images: File[];
  recommend: string;
}

interface ReviewWritePcModalProps {
  isOpen: boolean;
  productImage: string;
  onClose: () => void;
  onSubmit: (values: ReviewWritePcModalValues) => Promise<void>;
}

const CompleteView = ({ onContinue }: { onContinue: () => void }) => (
  <CompleteContainer>
    <CompleteTitle>Thank you!</CompleteTitle>
    <CompleteDesc>Your review will be posted on product page and your profile</CompleteDesc>
    <CompleteButton onClick={onContinue}>Continue Shopping</CompleteButton>
  </CompleteContainer>
);

export const ReviewWritePcModal = ({ isOpen, productImage, onClose, onSubmit }: ReviewWritePcModalProps) => {
  const [isComplete, setIsComplete] = useState(false);
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [nickname, setNickname] = useState('');
  const [skinType, setSkinType] = useState('');
  const [skinConcern, setSkinConcern] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [recommend, setRecommend] = useState<string>('');

  // taxonomy 데이터 fetch (모달이 열릴 때만)
  const { data: taxonomy, isLoading } = useQuery({
    queryKey: ['taxonomy'],
    queryFn: ProductApi.getTaxonomy,
    enabled: isOpen,
  });

  // 옵션 변환
  const skinTypeOptions: SelectOption[] =
    taxonomy?.skinType?.map((item) => ({
      label: item.name.split(' ')?.at(0) || '',
      value: item.name.split(' ')?.at(0) || '', // @TODO: API has problem
    })) || [];
  const skinConcernOptions: SelectOption[] =
    taxonomy?.skinConcern?.map((item) => ({
      label: item.name,
      value: item.name,
    })) || [];

  const isSubmitDisabled =
    rating === 0 || !content.trim() || !title.trim() || !nickname.trim() || nickname.length < 4 || nickname.length > 25;

  const handleSubmit = async () => {
    try {
      await onSubmit({
        rating,
        title,
        content,
        nickname,
        skinType,
        skinConcern,
        images,
        recommend,
      });
      setIsComplete(true);
    } catch {
      // 에러 처리 필요시 여기에 작성
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setIsComplete(false);
      setRating(0);
      setTitle('');
      setContent('');
      setNickname('');
      setSkinType('');
      setSkinConcern('');
      setImages([]);
      setRecommend('');
    }
    return () => {
      document.body.style.overflow = '';
      setIsComplete(false);
      setRating(0);
      setTitle('');
      setContent('');
      setNickname('');
      setSkinType('');
      setSkinConcern('');
      setImages([]);
      setRecommend('');
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <Overlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
          <Modal
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 500 }}
            onClick={(e) => e.stopPropagation()}
          >
            {!isComplete && (
              <Header>
                <Title>Review this product</Title>
                <CloseButton onClick={onClose}>×</CloseButton>
              </Header>
            )}
            <Content>
              {!isComplete ? (
                <ReviewWritePcForm
                  rating={rating}
                  setRating={setRating}
                  title={title}
                  setTitle={setTitle}
                  content={content}
                  setContent={setContent}
                  nickname={nickname}
                  setNickname={setNickname}
                  skinType={skinType}
                  setSkinType={setSkinType}
                  skinConcern={skinConcern}
                  setSkinConcern={setSkinConcern}
                  images={images}
                  setImages={setImages}
                  recommend={recommend}
                  setRecommend={setRecommend}
                  isSubmitDisabled={isSubmitDisabled}
                  onSubmit={handleSubmit}
                  productImage={productImage}
                  skinTypeOptions={skinTypeOptions}
                  skinConcernOptions={skinConcernOptions}
                />
              ) : (
                <CompleteView onContinue={onClose} />
              )}
            </Content>
          </Modal>
        </Overlay>
      )}
    </AnimatePresence>
  );
};
